import { flowControlSlotIdsEqual } from '@/helpers/flow-control-playing-segment.helper';
import { matchesLiveCueCurrentStep, resolveCueItemState } from '@/helpers/perform-staging.helper';
import {
  isNextCuePreviewTarget,
  resolveLiveNextCueTarget,
  type LiveNextCueTarget,
} from '@/helpers/resolve-next-cue-preview-row.helper';
import type { SimulationCuePlaylistContext } from '@/helpers/simulation-cue-engine.helper';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';

/** Safety unlock if cue next never refreshes after a jump. */
export const NEXT_CUE_RECALCULATING_WATCHDOG_MS = 15_000;

export type NextCueRecalculatingLatch = {
  entityRef: string;
  /** Jump target media id when known (`playMedia`); omit for unresolved segment jumps. */
  targetItemId?: string;
  targetContext?: SimulationCuePlaylistContext;
  targetExpandId?: string;
  expectedNext?: LiveNextCueTarget;
  expectedNextExpandId?: string;
  prePointer: number;
  preNextKey: string;
  latchedAtMs: number;
};

/** Stable identity of projected Next from cueState. */
export function buildNextCueProjectionKey(
  cueState: Pick<FlowCueStateV1, 'nextItemId' | 'nextCueItemId' | 'nextScope' | 'nextSlotId'> | null | undefined,
): string {
  if (!cueState) {
    return '';
  }
  return [
    cueState.nextItemId ?? '',
    cueState.nextCueItemId ?? '',
    cueState.nextScope ?? '',
    cueState.nextSlotId ?? '',
  ].join('|');
}

export function createNextCueRecalculatingLatch(params: {
  entityRef: string;
  cueState: FlowCueStateV1 | null | undefined;
  targetItemId?: string;
  targetContext?: SimulationCuePlaylistContext;
  targetExpandId?: string;
  expectedNext?: LiveNextCueTarget;
  expectedNextExpandId?: string;
  nowMs?: number;
}): NextCueRecalculatingLatch {
  return {
    entityRef: params.entityRef,
    targetItemId: params.targetItemId,
    targetContext: params.targetContext,
    targetExpandId: params.targetExpandId,
    expectedNext: params.expectedNext,
    expectedNextExpandId: params.expectedNextExpandId,
    prePointer: params.cueState?.pointer ?? 0,
    preNextKey: buildNextCueProjectionKey(params.cueState),
    latchedAtMs: params.nowMs ?? Date.now(),
  };
}

/**
 * List live chrome follows `currentStep` as soon as the pointer lands — `items.phase`
 * can stay `preloading` until playout.onAir is synced after perform.
 */
export function isJumpCurrentIndicatorReady(
  latch: NextCueRecalculatingLatch,
  cueState: FlowCueStateV1 | null | undefined,
): boolean {
  if (!latch.targetItemId) {
    const pointer = cueState?.pointer;
    return pointer != null && pointer !== latch.prePointer;
  }
  if (cueState && matchesLiveCueCurrentStep(cueState, latch.targetItemId, latch.targetContext)) {
    return true;
  }
  const entry = resolveCueItemState(cueState?.items, latch.targetItemId, latch.targetContext);
  return entry?.phase === 'on_air' || entry?.isCurrent === true;
}

/**
 * List next chrome follows cueState next projection. BE skip/playability can differ from the
 * FE jump prediction — a moved projection is enough; do not wait 15s for an exact slot match.
 */
export function isJumpNextIndicatorReady(
  latch: NextCueRecalculatingLatch,
  cueState: FlowCueStateV1 | null | undefined,
): boolean {
  const nextKey = buildNextCueProjectionKey(cueState);
  const projectionMoved = nextKey !== latch.preNextKey;
  if (!latch.expectedNext?.itemId) {
    return projectionMoved || isJumpCurrentIndicatorReady(latch, cueState);
  }
  if (
    isNextCuePreviewTarget(resolveLiveNextCueTarget(cueState ?? undefined), latch.expectedNext.itemId, {
      scope: latch.expectedNext.scope,
      slotId: latch.expectedNext.slotId,
      cueItemId: latch.expectedNext.cueItemId,
    })
  ) {
    return true;
  }
  return projectionMoved;
}

/**
 * After a jump/segment take, hide stale Next until the rundown can paint Current + Next,
 * or error/watchdog.
 */
export function resolveNextCueRecalculating(params: {
  latch: NextCueRecalculatingLatch | null | undefined;
  cueState: FlowCueStateV1 | null | undefined;
  mutationFailed?: boolean;
  nowMs?: number;
  watchdogMs?: number;
}): { recalculating: boolean; clearLatch: boolean; watchdogFired: boolean } {
  const latch = params.latch;
  if (!latch) {
    return { recalculating: false, clearLatch: false, watchdogFired: false };
  }

  if (params.mutationFailed) {
    return { recalculating: false, clearLatch: true, watchdogFired: false };
  }

  const nowMs = params.nowMs ?? Date.now();
  const watchdogMs = params.watchdogMs ?? NEXT_CUE_RECALCULATING_WATCHDOG_MS;
  if (nowMs - latch.latchedAtMs >= watchdogMs) {
    return { recalculating: false, clearLatch: true, watchdogFired: true };
  }

  const currentReady = isJumpCurrentIndicatorReady(latch, params.cueState);
  const nextReady = isJumpNextIndicatorReady(latch, params.cueState);
  if (currentReady && nextReady) {
    return { recalculating: false, clearLatch: true, watchdogFired: false };
  }

  return { recalculating: true, clearLatch: false, watchdogFired: false };
}

function latchTargetAsPreview(latch: NextCueRecalculatingLatch): LiveNextCueTarget | null {
  if (!latch.targetItemId) {
    return null;
  }
  return {
    itemId: latch.targetItemId,
    cueItemId: latch.targetContext?.cueItemId,
    scope: latch.targetContext?.scope,
    slotId: latch.targetContext?.slotId,
  };
}

/** Spinner on the jump target until its live chrome paints, and on predicted Next until next chrome paints. */
export function isCueListItemIndicatorPending(params: {
  latch: NextCueRecalculatingLatch | null | undefined;
  cueState: FlowCueStateV1 | null | undefined;
  itemId: string;
  playlistContext?: SimulationCuePlaylistContext;
}): boolean {
  const latch = params.latch;
  if (!latch) {
    return false;
  }

  const target = latchTargetAsPreview(latch);
  const isTarget = target != null && isNextCuePreviewTarget(target, params.itemId, params.playlistContext);
  if (isTarget && !isJumpCurrentIndicatorReady(latch, params.cueState)) {
    return true;
  }

  const expectedNext = latch.expectedNext;
  const isExpectedNext =
    expectedNext != null && isNextCuePreviewTarget(expectedNext, params.itemId, params.playlistContext);
  return isExpectedNext && !isJumpNextIndicatorReady(latch, params.cueState);
}

/** Spinner on the section that will become live / next until those fills appear. */
export function isCueListSegmentIndicatorPending(params: {
  latch: NextCueRecalculatingLatch | null | undefined;
  segmentExpandId: string | null | undefined;
  isSegmentPlaying: boolean;
  isNextCueSegment: boolean;
}): boolean {
  const latch = params.latch;
  const segmentExpandId = params.segmentExpandId;
  if (!latch || !segmentExpandId) {
    return false;
  }

  if (
    latch.targetExpandId &&
    flowControlSlotIdsEqual(segmentExpandId, latch.targetExpandId) &&
    !params.isSegmentPlaying
  ) {
    return true;
  }

  return Boolean(
    latch.expectedNextExpandId &&
    flowControlSlotIdsEqual(segmentExpandId, latch.expectedNextExpandId) &&
    !params.isSegmentPlaying &&
    !params.isNextCueSegment,
  );
}
